/**
 * The master elements the caller may still add to one unit (f11.2).
 *
 * The search text is debounced 250 ms before it becomes part of the query
 * key, so typing does not fire one request per keystroke. The server decides
 * everything about the list: master membership, scope, grant, duplicates.
 */

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export const CANDIDATE_DEBOUNCE_MS = 250;

export function useDebounced(value: string, delay = CANDIDATE_DEBOUNCE_MS): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

export function useElementCandidates(cycleId: string, businessUnitId: string, search: string) {
  const q = useDebounced(search.trim());
  return useQuery({
    queryKey: queryKeys.cycle.forecastElementCandidates(cycleId, businessUnitId, q),
    queryFn: () => api.listForecastElementCandidates(cycleId, businessUnitId, q),
    enabled: Boolean(cycleId && businessUnitId),
    placeholderData: keepPreviousData,
  });
}
